import { useId } from 'react';
import { useTranslation } from 'next-i18next';

import Modal from '@components/global/Modal';
import TextButton from '_components/UI/TextButton';
import QuickAddPopover from '_components/QuickAdd/QuickAddPopover';
import { CartAddIcon } from '_components/QuickAdd/QuickAddTrigger';
import type { BundleStripProduct } from '@adapters/prismic/transforms/bundleStrip';
import type {
  BundleSelectionLine,
  BundleSelectionStatus,
} from '../useBundleSelection';

import styles from './BundleAddModal.module.scss';

export interface BundleAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  products: BundleStripProduct[];
  status: BundleSelectionStatus;
  lines: BundleSelectionLine[];
  formatLinePrice: (line: BundleSelectionLine) => {
    displayPrice: string;
    salePrice?: string;
  };
  total: string | null;
  canAdd: boolean;
  isAdding: boolean;
  message: string | null;
  onSelectColour: (code: string, colourId: number) => void;
  onSelectSize: (code: string, sizeId: number) => void;
  onConfirm: () => void;
  onRetry: () => void;
}

const BundleAddModal = ({
  isOpen,
  onClose,
  products,
  status,
  lines,
  formatLinePrice,
  total,
  canAdd,
  isAdding,
  message,
  onSelectColour,
  onSelectSize,
  onConfirm,
  onRetry,
}: BundleAddModalProps) => {
  const { t } = useTranslation('common');
  const descriptionId = useId();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('bundleStrip.modalTitle')}
      bottomPositioning
      containerClassNames={styles.container}
    >
      <p id={descriptionId} className={styles.description}>
        {t('bundleStrip.modalDescription')}
      </p>

      {status === 'loading' && (
        <div className={styles.loading} role="status">
          <QuickAddPopover.Spinner />
          <span className={styles.srOnly}>{t('bundleStrip.loading')}</span>
        </div>
      )}

      {status === 'error' && (
        <div className={styles.error} role="alert">
          <p>{t('bundleStrip.loadError')}</p>
          <TextButton
            type="secondary"
            label={t('bundleStrip.retry')}
            onClick={onRetry}
          />
        </div>
      )}

      {status === 'ready' && (
        <ul className={styles.lines} aria-describedby={descriptionId}>
          {lines.map((line) => {
            const record = products.find(
              ({ product }) => product.parentSku === line.code
            )?.product;
            const name = record?.title ?? line.product.name ?? '';
            const showUnavailable =
              !line.isUnsupported &&
              !line.canAddToCart &&
              (line.sizes.length === 0 || line.selectedSizeId != null);

            return (
              <li key={line.code} className={styles.line}>
                <QuickAddPopover.ProductSummary
                  name={name}
                  imageSrc={
                    record?.imageTransparent ||
                    record?.image ||
                    '/images/pdp/product-placeholder.jpg'
                  }
                  {...formatLinePrice(line)}
                />
                {line.quantity > 1 && (
                  <p className={styles.lineQuantity}>
                    {t('bundleStrip.quantity', { quantity: line.quantity })}
                  </p>
                )}
                {line.isUnsupported ? (
                  <p className={styles.lineMessage}>
                    <a href={record?.url}>
                      {t('bundleStrip.unsupported', { name })}
                    </a>
                  </p>
                ) : (
                  <>
                    {line.colours.length > 1 && (
                      <QuickAddPopover.ColourRow
                        colours={line.colours}
                        selectedColourId={line.selectedColourId}
                        onColourSelect={(colour) =>
                          onSelectColour(line.code, colour.id)
                        }
                      />
                    )}
                    {line.sizes.length > 1 && (
                      <QuickAddPopover.SizeGrid
                        sizes={line.sizes}
                        selectedSizeId={line.selectedSizeId}
                        onSizeSelect={(size) =>
                          onSelectSize(line.code, size.id)
                        }
                      />
                    )}
                    {showUnavailable && (
                      <p className={styles.lineMessage}>
                        {t('bundleStrip.unavailable', { name })}
                      </p>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className={styles.footer}>
        {total && <p className={styles.total}>{total}</p>}
        <TextButton
          type="primary"
          size="medium"
          className={styles.cta}
          label={isAdding ? t('bundleStrip.adding') : t('bundleStrip.addAll')}
          leadingIcon={CartAddIcon}
          leadingIconSize={24}
          disabled={!canAdd || isAdding}
          onClick={onConfirm}
        />
        <p className={styles.message} role="status">
          {message}
        </p>
      </div>
    </Modal>
  );
};

export default BundleAddModal;
